/**
 * Pure form logic for the notify-me modal: channel rules, validation,
 * signup payload building and backend-result mapping. Kept DOM-free so it is
 * testable in the node environment and reusable by other render layers.
 */
import type { SignupInput, SignupResult, StoqSettings } from '../types'
import { toNumericId } from '../util'
import { variantIdOf } from '../variant-input'
import type { ModalOptions } from './types'

/** Which contact inputs the form shows. */
export interface FormChannels {
  email: boolean
  phone: boolean
}

/**
 * Channel rules: email is always offered unless the shop explicitly disabled
 * it AND offers SMS instead; phone is offered only when the shop has SMS
 * enabled. The form never ends up with zero channels.
 */
export function resolveChannels(settings: Readonly<StoqSettings>): FormChannels {
  const phone = settings.sms_enabled === true
  const email = settings.email_enabled !== false || !phone
  return { email, phone }
}

/** Raw input values as typed by the shopper. */
export interface FormValues {
  email?: string
  phone?: string
}

export type FormErrorKind = 'email' | 'phone' | 'empty' | 'duplicate' | 'generic'

export interface ValidationResult {
  /** Field-level problems. Empty when the form may be submitted. */
  errors: FormErrorKind[]
  /** Cleaned email, present only when provided and valid. */
  email?: string
  /** Cleaned E.164-ish phone (`+<digits>`), present only when provided and valid. */
  phone?: string
}

// Matches URI::MailTo::EMAIL_REGEXP used by the backend's Customer validation
// (same regex the theme embed uses).
const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/

export function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email)
}

/** Strips formatting characters; keeps a leading `+`. */
export function normalizePhone(raw: string): string {
  return raw.trim().replace(/[\s\-().]/g, '')
}

/**
 * Validates the raw inputs against the active channels. Both inputs are
 * optional individually, but at least one valid contact is required. Phone
 * numbers must include the country code (there is no country picker); a
 * missing leading `+` is added.
 */
export function validateForm(values: FormValues, channels: FormChannels): ValidationResult {
  const errors: FormErrorKind[] = []
  const result: ValidationResult = { errors }

  const email = (values.email ?? '').trim()
  if (channels.email && email !== '') {
    if (isValidEmail(email)) {
      result.email = email
    } else {
      errors.push('email')
    }
  }

  const phone = normalizePhone(values.phone ?? '')
  if (channels.phone && phone !== '') {
    if (/^\+?\d{5,15}$/.test(phone)) {
      result.phone = phone.startsWith('+') ? phone : `+${phone}`
    } else {
      errors.push('phone')
    }
  }

  if (errors.length === 0 && result.email === undefined && result.phone === undefined) {
    errors.push('empty')
  }

  return result
}

/** Builds the {@link StoqClient.createSignup} input from validated values. */
export function buildSignupInput(
  options: Pick<ModalOptions, 'variantId' | 'productId' | 'quantity' | 'prefill'>,
  validated: Pick<ValidationResult, 'email' | 'phone'>,
): SignupInput {
  const input: SignupInput = {
    variantId: variantIdOf(options.variantId),
    productId: toNumericId(options.productId),
    quantity: options.quantity ?? 1,
  }
  if (validated.email !== undefined) input.email = validated.email
  if (validated.phone !== undefined) input.phone = validated.phone
  // A single-channel signup is tagged so the backend creates only that
  // intent; with both contacts the backend creates one intent per channel.
  if (validated.email !== undefined && validated.phone === undefined) input.channel = 'email'
  if (validated.phone !== undefined && validated.email === undefined) input.channel = 'sms'
  if (options.prefill?.name !== undefined) input.name = options.prefill.name
  return input
}

/** Final state of a submit attempt. */
export type SubmitOutcome =
  | { state: 'success' }
  | { state: 'already-registered' }
  | { state: 'error'; kind: 'email' | 'phone' | 'generic' }

/**
 * Maps the intents endpoint response to a UI state. Error shapes mirror what
 * the theme embed handles: `already_registered`, nested
 * `email_errors`/`sms_errors` (where an `intent` sub-error means a duplicate
 * signup) and flat `email`/`phone` model errors.
 */
export function mapSubmitResult(result: SignupResult): SubmitOutcome {
  if (result.ok) return { state: 'success' }

  const errors = result.errors
  if (errors !== null && typeof errors === 'object' && !Array.isArray(errors)) {
    const e = errors as Record<string, unknown>
    const emailErrors = e['email_errors'] as Record<string, unknown> | undefined
    const smsErrors = e['sms_errors'] as Record<string, unknown> | undefined
    if (e['already_registered'] || emailErrors?.['intent'] || smsErrors?.['intent']) {
      return { state: 'already-registered' }
    }
    if (emailErrors || e['email']) return { state: 'error', kind: 'email' }
    if (smsErrors || e['phone']) return { state: 'error', kind: 'phone' }
  }
  return { state: 'error', kind: 'generic' }
}

/** True when the created intents include an email intent (for opt-in copy). */
export function usedEmailChannel(
  validated: Pick<ValidationResult, 'email'>,
  intents: unknown[],
): boolean {
  if (validated.email === undefined) return false
  if (intents.length === 0) return true
  return intents.some(
    (intent) =>
      intent !== null
      && typeof intent === 'object'
      && (intent as Record<string, unknown>)['channel'] === 'email',
  )
}
